import React, { useCallback } from 'react'

import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { css } from '@emotion/react'
import { useSelector } from 'react-redux'

import { withDeferedRender } from 'util/hoc'
import { selectCurrentContactIsMissing } from 'ducks/crm/contacts'

import Loader from '../Loader'
import {
  selectContactDetailsColumnWidgetCardsByKey,
  selectContactDetailsContactInformationCard,
} from './selectors'

import WidgetCard from './WidgetCard'
import MissingContact from './MissingContact'
import ConversationCustomField from './cards/ConversationCustomField'
import { SortableActivatorContext } from '../SortableVisibleControlsWithTitle'

export default React.memo(withDeferedRender(WidgetCards, Loader))

const hiddenDefaultCustomFieldCategoryPatterns = [
  /^customFieldCategory.shopify_/,
  /^customFieldCategory.recharge_/,
]

function shouldRenderCard({ key: cardKey, visible }, cards) {
  if (visible === false) return false
  const card = cards[cardKey]
  if (!card) return false
  if (cardKey === 'customFieldCategory.contact_information') return false
  if (cardKey.startsWith('customFieldCategory.cfm_')) return false
  return !hiddenDefaultCustomFieldCategoryPatterns.some(pattern =>
    cardKey.match(pattern)
  )
}

const Card = ({ cards, cardKey, visible, index, doHideCard }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id: `${index}` })
  if (!shouldRenderCard({ key: cardKey, visible }, cards)) return null

  const card = cards[cardKey]
  return (
    <div
      ref={setNodeRef}
      className="SortableCategory WidgetCard"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <SortableActivatorContext.Provider
        value={{ attributes, listeners, setActivatorNodeRef }}
      >
        <WidgetCard doHideCard={doHideCard} widgetCard={card} />
      </SortableActivatorContext.Provider>
    </div>
  )
}

const isLoadingCss = css`
  display: none;
`
function WidgetCards({ cardOrder, doHideCard, doReorderItems, isLoading }) {
  const isMissing = useSelector(selectCurrentContactIsMissing)
  const cards = useSelector(selectContactDetailsColumnWidgetCardsByKey)
  const contactInformationCard = useSelector(
    selectContactDetailsContactInformationCard
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )
  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      doReorderItems({ oldIndex: Number(active.id), newIndex: Number(over.id) })
    },
    [doReorderItems]
  )

  if (isMissing) return <MissingContact />
  if (!cards || !cardOrder) return null

  const sortableCards = cardOrder
    .map((card, index) => ({ ...card, index }))
    .filter(card => shouldRenderCard(card, cards))
  const sortableCardIds = sortableCards.map(({ index }) => `${index}`)

  return (
    <>
      {isLoading && <Loader />}
      <div css={isLoading && isLoadingCss}>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={sortableCardIds}
            strategy={verticalListSortingStrategy}
          >
            {contactInformationCard && (
              <WidgetCard
                reorderable={false}
                widgetCard={contactInformationCard}
              />
            )}
            <ConversationCustomField />
            {sortableCards.map(({ key, visible, index }) => (
              <Card
                cardKey={key}
                key={key}
                visible={visible}
                index={index}
                cards={cards}
                doHideCard={doHideCard}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </>
  )
}
